// Input validation and sanitization service for APN
import DOMPurify from 'dompurify';

// Validation patterns
const VALIDATION_PATTERNS = {
  email: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
  phone: /^[\+]?[1-9][\d]{0,15}$/,
  url: /^https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)$/,
  alphanumeric: /^[a-zA-Z0-9\s]+$/,
  noSpecialChars: /^[a-zA-Z0-9\s\-_.,!?]+$/,
  strongPassword: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
  username: /^[a-zA-Z0-9_-]{3,20}$/,
  zipCode: /^\d{5}(-\d{4})?$/,
  linkedin: /^https:\/\/(www\.)?linkedin\.com\/in\/[a-zA-Z0-9-]+\/?$/
};

// Maximum lengths for different field types
const MAX_LENGTHS = {
  shortText: 100,
  mediumText: 500,
  longText: 2000,
  description: 5000,
  title: 200,
  name: 50,
  company: 100,
  position: 100,
  location: 100,
  phone: 20,
  url: 2000
};

// Sanitization options
const SANITIZE_OPTIONS = {
  basic: {
    ALLOWED_TAGS: [],
    ALLOWED_ATTR: []
  },
  rich: {
    ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 'ol', 'ul', 'li', 'a'],
    ALLOWED_ATTR: ['href', 'target']
  },
  minimal: {
    ALLOWED_TAGS: ['br', 'strong', 'em'],
    ALLOWED_ATTR: []
  }
};

/**
 * Sanitize HTML content to prevent XSS attacks
 * @param {string} input - The input to sanitize
 * @param {string} level - Sanitization level: 'basic', 'rich', 'minimal'
 * @returns {string} Sanitized content
 */
export const sanitizeHtml = (input, level = 'basic') => {
  if (!input || typeof input !== 'string') return '';
  
  const options = SANITIZE_OPTIONS[level] || SANITIZE_OPTIONS.basic;
  return DOMPurify.sanitize(input, options);
};

/**
 * Validate email address
 * @param {string} email - Email to validate
 * @returns {object} Validation result
 */
export const validateEmail = (email) => {
  if (!email) return { isValid: false, error: 'Email is required' };
  if (email.length > MAX_LENGTHS.shortText) return { isValid: false, error: 'Email is too long' };
  if (!VALIDATION_PATTERNS.email.test(email)) return { isValid: false, error: 'Invalid email format' };
  return { isValid: true };
};

/**
 * Validate phone number
 * @param {string} phone - Phone number to validate
 * @returns {object} Validation result
 */
export const validatePhone = (phone) => {
  if (!phone) return { isValid: false, error: 'Phone number is required' };
  if (phone.length > MAX_LENGTHS.phone) return { isValid: false, error: 'Phone number is too long' };
  if (!VALIDATION_PATTERNS.phone.test(phone)) return { isValid: false, error: 'Invalid phone number format' };
  return { isValid: true };
};

/**
 * Validate URL
 * @param {string} url - URL to validate
 * @param {boolean} required - Whether URL is required
 * @returns {object} Validation result
 */
export const validateUrl = (url, required = false) => {
  if (!url) return required ? { isValid: false, error: 'URL is required' } : { isValid: true };
  if (url.length > MAX_LENGTHS.url) return { isValid: false, error: 'URL is too long' };
  if (!VALIDATION_PATTERNS.url.test(url)) return { isValid: false, error: 'Invalid URL format' };
  return { isValid: true };
};

/**
 * Validate text input
 * @param {string} text - Text to validate
 * @param {object} options - Validation options
 * @returns {object} Validation result
 */
export const validateText = (text, options = {}) => {
  const {
    required = false,
    minLength = 0,
    maxLength = MAX_LENGTHS.longText,
    pattern = null,
    fieldName = 'Field'
  } = options;

  if (!text || text.trim() === '') {
    return required ? { isValid: false, error: `${fieldName} is required` } : { isValid: true };
  }

  const trimmedText = text.trim();
  
  if (trimmedText.length < minLength) {
    return { isValid: false, error: `${fieldName} must be at least ${minLength} characters` };
  }
  
  if (trimmedText.length > maxLength) {
    return { isValid: false, error: `${fieldName} must be less than ${maxLength} characters` };
  }
  
  if (pattern && !pattern.test(trimmedText)) {
    return { isValid: false, error: `${fieldName} contains invalid characters` };
  }
  
  return { isValid: true, value: trimmedText };
};

/**
 * Validate password strength
 * @param {string} password - Password to validate
 * @returns {object} Validation result
 */
export const validatePassword = (password) => {
  if (!password) return { isValid: false, error: 'Password is required' };
  if (password.length < 8) return { isValid: false, error: 'Password must be at least 8 characters' };
  if (password.length > 128) return { isValid: false, error: 'Password is too long' };
  if (!VALIDATION_PATTERNS.strongPassword.test(password)) {
    return { 
      isValid: false, 
      error: 'Password must contain uppercase, lowercase, number, and special character' 
    };
  }
  return { isValid: true };
};

/**
 * Validate LinkedIn URL
 * @param {string} url - LinkedIn URL to validate
 * @param {boolean} required - Whether URL is required
 * @returns {object} Validation result
 */
export const validateLinkedInUrl = (url, required = false) => {
  if (!url) return required ? { isValid: false, error: 'LinkedIn URL is required' } : { isValid: true };
  if (!VALIDATION_PATTERNS.linkedin.test(url)) {
    return { isValid: false, error: 'Invalid LinkedIn URL format' };
  }
  return { isValid: true };
};

/**
 * Validate user profile data
 * @param {object} profileData - Profile data to validate
 * @returns {object} Validation result
 */
export const validateUserProfile = (profileData) => {
  const errors = {};
  
  // Required fields
  const nameValidation = validateText(profileData.name, {
    required: true,
    maxLength: MAX_LENGTHS.name,
    fieldName: 'Name'
  });
  if (!nameValidation.isValid) errors.name = nameValidation.error;
  
  const emailValidation = validateEmail(profileData.email);
  if (!emailValidation.isValid) errors.email = emailValidation.error;
  
  // Optional fields
  if (profileData.phone) {
    const phoneValidation = validatePhone(profileData.phone);
    if (!phoneValidation.isValid) errors.phone = phoneValidation.error;
  }
  
  if (profileData.company) {
    const companyValidation = validateText(profileData.company, {
      maxLength: MAX_LENGTHS.company,
      fieldName: 'Company'
    });
    if (!companyValidation.isValid) errors.company = companyValidation.error;
  }
  
  if (profileData.position) {
    const positionValidation = validateText(profileData.position, {
      maxLength: MAX_LENGTHS.position,
      fieldName: 'Position'
    });
    if (!positionValidation.isValid) errors.position = positionValidation.error;
  }
  
  if (profileData.linkedinUrl) {
    const linkedinValidation = validateLinkedInUrl(profileData.linkedinUrl);
    if (!linkedinValidation.isValid) errors.linkedinUrl = linkedinValidation.error;
  }
  
  if (profileData.bio) {
    const bioValidation = validateText(profileData.bio, {
      maxLength: MAX_LENGTHS.description,
      fieldName: 'Bio'
    });
    if (!bioValidation.isValid) errors.bio = bioValidation.error;
  }
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

/**
 * Validate job posting data
 * @param {object} jobData - Job data to validate
 * @returns {object} Validation result
 */
export const validateJobPosting = (jobData) => {
  const errors = {};
  
  const titleValidation = validateText(jobData.title, {
    required: true,
    maxLength: MAX_LENGTHS.title,
    fieldName: 'Job Title'
  });
  if (!titleValidation.isValid) errors.title = titleValidation.error;
  
  const descriptionValidation = validateText(jobData.description, {
    required: true,
    maxLength: MAX_LENGTHS.description,
    fieldName: 'Job Description'
  });
  if (!descriptionValidation.isValid) errors.description = descriptionValidation.error;
  
  const companyValidation = validateText(jobData.company, {
    required: true,
    maxLength: MAX_LENGTHS.company,
    fieldName: 'Company'
  });
  if (!companyValidation.isValid) errors.company = companyValidation.error;
  
  const locationValidation = validateText(jobData.location, {
    required: true,
    maxLength: MAX_LENGTHS.location,
    fieldName: 'Location'
  });
  if (!locationValidation.isValid) errors.location = locationValidation.error;
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

/**
 * Validate project posting data
 * @param {object} projectData - Project data to validate
 * @returns {object} Validation result
 */
export const validateProjectPosting = (projectData) => {
  const errors = {};
  
  const titleValidation = validateText(projectData.title, {
    required: true,
    maxLength: MAX_LENGTHS.title,
    fieldName: 'Project Title'
  });
  if (!titleValidation.isValid) errors.title = titleValidation.error;
  
  const descriptionValidation = validateText(projectData.description, {
    required: true,
    maxLength: MAX_LENGTHS.description,
    fieldName: 'Project Description'
  });
  if (!descriptionValidation.isValid) errors.description = descriptionValidation.error;
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

/**
 * Sanitize and validate comment content
 * @param {string} content - Comment content
 * @returns {object} Validation result with sanitized content
 */
export const validateComment = (content) => {
  if (!content || content.trim() === '') {
    return { isValid: false, error: 'Comment cannot be empty' };
  }
  
  const sanitized = sanitizeHtml(content, 'minimal');
  
  if (sanitized.length > MAX_LENGTHS.longText) {
    return { isValid: false, error: 'Comment is too long' };
  }
  
  return { isValid: true, value: sanitized };
};

// Export validation patterns and constants for use in components
export { VALIDATION_PATTERNS, MAX_LENGTHS };
